import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import axiosClient from '../../axios'

// ── Types ────────────────────────────────────────────────────────────────

export interface CampaignList {
  id: string
  name: string
  /** Résolues par le serveur : une liste dynamique n'en stocke aucune. */
  emails: string[]
  count: number
  /** 'static' = les adresses enregistrées ; 'app_users' = les comptes de l'application. */
  source?: string
  /** Vrai quand la liste se recalcule à chaque lecture au lieu de porter ses adresses. */
  dynamic?: boolean
  createdAt: string
  updatedAt: string
}

export interface CampaignListsResponse {
  data: CampaignList[]
}

export interface UpsertCampaignListPayload {
  name: string
  /** One email per line/comma — the backend splits, validates and dedupes. */
  emails: string
}

export interface UpsertCampaignListResponse extends CampaignList {
  invalidEmails: string[]
}

// ── Query keys ───────────────────────────────────────────────────────────

const campaignListsKey = ['admin', 'campaign-lists'] as const

// ── Hooks ────────────────────────────────────────────────────────────────

/** Saved recipient lists (e.g. "CHUs") — the compose form's list picker. */
export function useCampaignLists() {
  return useQuery({
    queryKey: campaignListsKey,
    queryFn: async () => {
      const { data } = await axiosClient.get<CampaignListsResponse>('/admin/campaign-lists')
      return data.data
    },
    staleTime: 15_000,
  })
}

export function useCreateCampaignList() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (payload: UpsertCampaignListPayload) => {
      const { data } = await axiosClient.post<UpsertCampaignListResponse>('/admin/campaign-lists', payload)
      return data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: campaignListsKey })
    },
  })
}

export function useUpdateCampaignList() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, ...payload }: UpsertCampaignListPayload & { id: string }) => {
      const { data } = await axiosClient.put<UpsertCampaignListResponse>(`/admin/campaign-lists/${id}`, payload)
      return data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: campaignListsKey })
    },
  })
}

export function useDeleteCampaignList() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => {
      const { data } = await axiosClient.delete(`/admin/campaign-lists/${id}`)
      return data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: campaignListsKey })
    },
  })
}
